import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Details.css';

import config from '../config';
const API_URL = config.API_URL;

interface RepoDetails {
    id: number;
    name: string;
    full_name: string;
    description: string;
    html_url: string;
    homepage: string;
    stargazers_count: number;
    forks_count: number;
    watchers_count: number;
    open_issues_count: number;
    language: string;
    created_at: string;
    updated_at: string;
    pushed_at: string;
    size: number;
    default_branch: string;
    topics: string[];
    license: { name: string } | null;
    owner: {
        login: string;
        avatar_url: string;
        html_url: string;
    };
}

interface Contributor {
    id: number;
    login: string;
    avatar_url: string;
    html_url: string;
    contributions: number;
}

interface LocalReview {
    id: string;
    oduserId: string;
    userName: string;
    rating: number;
    comment: string;
    createdAt: string;
}

const Details: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const [repo, setRepo] = useState<RepoDetails | null>(null);
    const [contributors, setContributors] = useState<Contributor[]>([]);
    const [reviews, setReviews] = useState<LocalReview[]>([]);
    const [loading, setLoading] = useState(true);
    const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
    const [isBookmarked, setIsBookmarked] = useState(false);

    const token = localStorage.getItem('token');
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const repoFullName = searchParams.get('name') || '';

    useEffect(() => {
        if (repoFullName) {
            fetchRepoDetails();
            fetchContributors();
            fetchLocalReviews();
            checkBookmarkStatus();
        }
    }, [repoFullName]);

    const fetchRepoDetails = async () => {
        setLoading(true);
        try {
            // Fetch from GitHub API (external)
            const response = await axios.get(`https://api.github.com/repos/${repoFullName}`);
            setRepo(response.data);
        } catch (error) {
            console.error('Failed to fetch repo details:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchContributors = async () => {
        try {
            const response = await axios.get(
                `https://api.github.com/repos/${repoFullName}/contributors`,
                { params: { per_page: 10 } }
            );
            setContributors(response.data);
        } catch (error) {
            console.error('Failed to fetch contributors:', error);
        }
    };

    const fetchLocalReviews = async () => {
        try {
            // Fetch reviews from YOUR local database
            const response = await axios.get(`${API_URL}/reviews/repo/${id}`);
            if (response.data.success) {
                setReviews(response.data.reviews);
            }
        } catch (error) {
            console.log('No local reviews yet');
        }
    };

    const checkBookmarkStatus = async () => {
        if (!token) return;
        try {
            const response = await axios.get(`${API_URL}/bookmarks/check/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsBookmarked(response.data.isBookmarked);
        } catch (error) {
            console.log('Bookmark check failed');
        }
    };

    const handleBookmark = async () => {
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            if (isBookmarked) {
                await axios.delete(`${API_URL}/bookmarks/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setIsBookmarked(false);
            } else {
                await axios.post(`${API_URL}/bookmarks`, {
                    repoId: id,
                    repoName: repo?.full_name,
                    repoDescription: repo?.description
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setIsBookmarked(true);
            }
        } catch (error) {
            console.error('Bookmark action failed');
        }
    };

    const handleSubmitReview = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            const response = await axios.post(`${API_URL}/reviews`, {
                repoId: id,
                repoName: repo?.full_name,
                rating: newReview.rating,
                comment: newReview.comment
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setReviews([response.data.review, ...reviews]);
                setNewReview({ rating: 5, comment: '' });
            }
        } catch (error) {
            console.error('Failed to submit review');
        }
    };

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatNumber = (num: number): string => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num.toString();
    };

    if (loading) {
        return (
            <div className="details-page">
                <div className="loading">Loading repository details...</div>
            </div>
        );
    }

    if (!repo) {
        return (
            <div className="details-page">
                <div className="error">Repository not found</div>
            </div>
        );
    }

    return (
        <div className="details-page">
            {/* Navigation */}
            <nav className="details-nav">
                <div className="nav-container">
                    <Link to="/" className="nav-logo">
                        <img src="/pngegg.png" alt="NU Connect" className="nav-logo-img" />
                        <span>NU Connect</span>
                    </Link>
                    <div className="nav-links">
                        <Link to="/" className="nav-link">Home</Link>
                        <Link to="/search" className="nav-link">Discover</Link>
                        <Link to="/chat" className="nav-link">Chat</Link>
                        {token ? (
                            <Link to="/profile" className="nav-link">Profile</Link>
                        ) : (
                            <Link to="/login" className="nav-btn primary">Sign In</Link>
                        )}
                    </div>
                </div>
            </nav>

            <div className="details-container">
                {/* Back Button */}
                <button onClick={() => navigate(-1)} className="back-btn">
                    ← Back to Search
                </button>

                {/* Repo Header */}
                <div className="repo-header">
                    <div className="repo-header-main">
                        <img src={repo.owner.avatar_url} alt={repo.owner.login} className="repo-owner-avatar" />
                        <div className="repo-header-info">
                            <h1>{repo.name}</h1>
                            <p className="repo-fullname">{repo.full_name}</p>
                            <p className="repo-description">{repo.description || 'No description'}</p>
                        </div>
                    </div>
                    <div className="repo-header-actions">
                        <button 
                            onClick={handleBookmark}
                            className={`btn-bookmark ${isBookmarked ? 'bookmarked' : ''}`}
                        >
                            {isBookmarked ? '★ Bookmarked' : '☆ Bookmark'}
                        </button>
                        <a href={repo.html_url} target="_blank" rel="noopener noreferrer" className="btn-github">
                            View on GitHub ↗
                        </a>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <span className="stat-icon">⭐</span>
                        <span className="stat-value">{formatNumber(repo.stargazers_count)}</span>
                        <span className="stat-label">Stars</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-icon">🍴</span>
                        <span className="stat-value">{formatNumber(repo.forks_count)}</span>
                        <span className="stat-label">Forks</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-icon">👁</span>
                        <span className="stat-value">{formatNumber(repo.watchers_count)}</span>
                        <span className="stat-label">Watchers</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-icon">🐛</span>
                        <span className="stat-value">{repo.open_issues_count}</span>
                        <span className="stat-label">Issues</span>
                    </div>
                </div>

                <div className="details-content">
                    {/* Main Info */}
                    <div className="main-section">
                        {/* About */}
                        <div className="info-card">
                            <h3>About</h3>
                            <div className="info-grid">
                                {repo.language && (
                                    <div className="info-item">
                                        <span className="info-label">Language</span>
                                        <span className="info-value">{repo.language}</span>
                                    </div>
                                )}
                                {repo.license && (
                                    <div className="info-item">
                                        <span className="info-label">License</span>
                                        <span className="info-value">{repo.license.name}</span>
                                    </div>
                                )}
                                <div className="info-item">
                                    <span className="info-label">Created</span>
                                    <span className="info-value">{formatDate(repo.created_at)}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Last Updated</span>
                                    <span className="info-value">{formatDate(repo.updated_at)}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Default Branch</span>
                                    <span className="info-value">{repo.default_branch}</span>
                                </div>
                                {repo.homepage && (
                                    <div className="info-item">
                                        <span className="info-label">Website</span>
                                        <a href={repo.homepage} target="_blank" rel="noopener noreferrer" className="info-link">
                                            {repo.homepage}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Topics */}
                        {repo.topics && repo.topics.length > 0 && (
                            <div className="info-card">
                                <h3>Topics</h3>
                                <div className="topics-list">
                                    {repo.topics.map(topic => (
                                        <Link 
                                            key={topic} 
                                            to={`/search?q=${topic}`}
                                            className="topic-tag"
                                        >
                                            {topic}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Reviews from Local Database */}
                        <div className="info-card">
                            <h3>Community Reviews ({reviews.length})</h3>
                            
                            {/* Review Form */}
                            {token ? (
                                <form onSubmit={handleSubmitReview} className="review-form">
                                    <div className="rating-select">
                                        <label>Rating:</label>
                                        <select 
                                            value={newReview.rating}
                                            onChange={(e) => setNewReview({...newReview, rating: parseInt(e.target.value)})}
                                        >
                                            {[5, 4, 3, 2, 1].map(n => (
                                                <option key={n} value={n}>{'⭐'.repeat(n)}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <textarea
                                        value={newReview.comment}
                                        onChange={(e) => setNewReview({...newReview, comment: e.target.value})}
                                        placeholder="Share your thoughts about this repository..."
                                        required
                                    />
                                    <button type="submit" className="btn-submit-review">
                                        Submit Review
                                    </button>
                                </form>
                            ) : (
                                <p className="login-prompt">
                                    <Link to="/login">Sign in</Link> to leave a review
                                </p>
                            )}

                            {/* Reviews List */}
                            <div className="reviews-list">
                                {reviews.length > 0 ? (
                                    reviews.map(review => (
                                        <div key={review.id} className="review-item">
                                            <div className="review-header">
                                                <Link to={`/profile/${review.oduserId}`} className="review-author">
                                                    {review.userName}
                                                </Link>
                                                <span className="review-rating">
                                                    {'⭐'.repeat(review.rating)}
                                                </span>
                                            </div>
                                            <p className="review-comment">{review.comment}</p>
                                            <span className="review-date">{formatDate(review.createdAt)}</span>
                                        </div>
                                    ))
                                ) : (
                                    <p className="no-reviews">No reviews yet. Be the first to review!</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="sidebar-section">
                        {/* Contributors */}
                        <div className="info-card">
                            <h3>Top Contributors</h3>
                            <ul className="contributors-list">
                                {contributors.map(contributor => (
                                    <li key={contributor.id} className="contributor-item">
                                        <a 
                                            href={contributor.html_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="contributor-link"
                                        >
                                            <img src={contributor.avatar_url} alt={contributor.login} />
                                            <span className="contributor-name">{contributor.login}</span>
                                            <span className="contributor-commits">{contributor.contributions} commits</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Owner */}
                        <div className="info-card">
                            <h3>Owner</h3>
                            <a 
                                href={repo.owner.html_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="owner-link"
                            >
                                <img src={repo.owner.avatar_url} alt={repo.owner.login} />
                                <span>{repo.owner.login}</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Details;